import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Building2, Check, AlertCircle, Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * MyClubs - shows all club memberships of the current user, lets them
 * see status (pending/approved) and switch active club.
 *
 * Only renders if multi_club_enabled feature flag is ON.
 */
const MyClubs = () => {
  const { user, switchOrganization } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [switching, setSwitching] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const flagsRes = await axios.get(`${API_BASE}/api/feature-flags`);
        if (cancelled) return;
        const isEnabled = !!flagsRes.data?.multi_club_enabled;
        setEnabled(isEnabled);
        if (!isEnabled) {
          setLoading(false);
          return;
        }
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE}/api/auth/memberships`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setMemberships(res.data?.memberships || []);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user?.email]);

  // Hide for super_admin or when feature is OFF or only one club
  if (!enabled || user?.role === 'super_admin') return null;
  if (loading) return null;
  if (memberships.length < 2) return null; // No point showing if only 1 club

  const handleSwitch = async (orgId) => {
    if (orgId === user?.organization_id) return;
    setSwitching(orgId);
    const result = await switchOrganization(orgId);
    setSwitching(null);
    if (result.success) {
      window.location.reload();
    }
  };

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700" data-testid="my-clubs-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
          <Building2 className="h-5 w-5 text-yellow-500" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">Mine klubber</h3>
          <p className="text-xs text-gray-400">Du er medlem i {memberships.length} klubber</p>
        </div>
      </div>

      <div className="space-y-2">
        {memberships.map((m) => {
          const isActive = m.organization_id === user?.organization_id;
          const isSwitching = switching === m.organization_id;
          const roleLabel = {
            'member': 'Medlem',
            'org_admin': 'Skyteleder',
            'admin': 'Admin',
            'range_officer': 'Skyteleder',
          }[m.role] || m.role;
          return (
            <div
              key={m.organization_id}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                isActive
                  ? 'bg-yellow-500/5 border-yellow-500/40'
                  : 'bg-gray-700/30 border-gray-700 hover:border-yellow-500/30'
              }`}
              data-testid={`my-club-item-${m.organization_id}`}
            >
              <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-gray-700 flex items-center justify-center overflow-hidden">
                {m.organization_logo ? (
                  <img src={m.organization_logo} alt="" className="h-full w-full object-cover" />
                ) : (
                  <Building2 className="h-5 w-5 text-yellow-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white truncate">{m.organization_name}</div>
                <div className="text-xs text-gray-400">
                  {roleLabel}{m.member_number ? ` · ${m.member_number}` : ''}
                </div>
              </div>
              {isActive ? (
                <span className="flex items-center gap-1 px-2 py-1 bg-yellow-500/10 text-yellow-400 text-xs rounded-full">
                  <Check className="h-3 w-3" />
                  Aktiv
                </span>
              ) : (
                <button
                  onClick={() => handleSwitch(m.organization_id)}
                  disabled={!!switching}
                  className="px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-yellow-400 hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
                  data-testid={`my-club-switch-${m.organization_id}`}
                >
                  {isSwitching ? 'Bytter...' : 'Bytt til'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg flex gap-2">
        <AlertCircle className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-blue-200">
          <strong>Vil du melde deg inn i en ny klubb?</strong> Skann QR-koden for egenregistrering i den klubben du besøker.
          Systemet gjenkjenner kontoen din automatisk og lar deg knytte den til den nye klubben.
        </div>
      </div>
    </div>
  );
};

export default MyClubs;
